import { Component, inject } from '@angular/core';
import { AppInformationService } from 'app/core/app-information.service';
import { AuthService, GenesisUnitOfWork } from 'app/core/core.module';
import { PartConfigWinchStand } from 'app/core/model/entity-model';
import { InputDialogComponent } from 'app/shared/input-dialog/input-dialog.component';
import  validationEngine from 'devextreme/ui/validation_engine';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { confirm } from 'devextreme/ui/dialog';

@Component({
    selector: 'app-winch-stand-dialog',
    template: `
        <div class="modal-header">
            <h2 class="modal-title">Winch Stand</h2>
        </div>
        <div class="flex-fill d-flex flex-column">
            <div class="card">
                <div class="card-body">
                    @if (winchStand) {
                        <dx-form [formData]="winchStand" validationGroup="validationGroup">
                            <dxi-item itemType="group">
                                <dxi-item dataField="name">
                                    <dxi-validation-rule type="required"> </dxi-validation-rule>
                                </dxi-item>
                                <dxi-item dataField="value">
                                    <dxi-validation-rule type="required"> </dxi-validation-rule>
                                </dxi-item>
                            </dxi-item>
                        </dx-form>
                    }
                    <div class="d-flex">
                        <div class="d-flex flex-column ms-3 me-3">
                            <table class="mt-3 table table-hover table-min-width">
                                <thead>
                                    Options
                                    <i class="ms-2 fa fa-plus" (click)="addOption()"></i>
                                </thead>
                                @if (winchStand && winchStand._options?.length > 0) {
                                    <tbody>
                                        <tr *ngFor="let option of winchStand._options">
                                            <td (click)="toggleDefault(option.id)">{{ option.name }}</td>
                                            <td></td>
                                            <td class="icon-collapse-horizontal">
                                                <i [ngClass]="{ fa: true, 'fa-star': option.isDefault, 'fa-star-o': !option.isDefault }" (click)="toggleDefault(option.id)"></i>
                                            </td>
                                            <td class="icon-collapse-horizontal"><i class="fa fa-times" (click)="deleteLink(option.id)"></i></td>
                                        </tr>
                                    </tbody>
                                }
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="modal-footer">
            <button class="btn btn-danger" (click)="save()">Save</button>
            <button class="btn btn-success" (click)="close()">Cancel</button>
        </div>
    `,
    styles: `
        .table-min-width {
            min-width: 150px;
        }
    `,
    standalone: false,
})
export class WinchStandDialogComponent {
    appInfo = inject(AppInformationService);
    auth = inject(AuthService);
    uow = inject(GenesisUnitOfWork);
    modalService = inject(BsModalService);

    result = new Subject<{ success: boolean }>();
    winchStand: PartConfigWinchStand;

    displayOrder: number;
    validationGroup: 'validationGroup';

    async ngOnInit() {
        await this.getData();
    }

    async getData() {
        if (!this.winchStand) {
            this.winchStand = this.uow.partConfigWinchStands.createEntity({
                displayOrder: this.displayOrder,
                created: new Date(),
                createdBy: this.auth.currentUser?.userName,
            }) as PartConfigWinchStand;
        }
    }
    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift({
            location: 'before',
            template: 'toolbarButtons',
        });
    }

    async addOption() {
        let options = await this.uow.partConfigWinchStandOptions.whereActive();
        options = options.filter((x) => !this.winchStand._options?.map((y) => y.optionId).includes(x.id));
        if (options.length === 0) {
            this.appInfo.notifyToast(`No more options to add`, 'info');
            return;
        }

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Add Option',
                dialogPrompt: 'Add',
                dialogValueArray: options,
                mode: 'selectWithId',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            console.log(result.value);
            console.log(this.winchStand.id)
            if (result.result && !!result.value) {
                const newLink = this.uow.partConfigWinchStandOptionLinks.createEntity({
                    partConfigWinchStandId: this.winchStand.id,
                    partConfigWinchStandOptionId: result.value,
                });
                this.winchStand._options.push({ optionId: result.value, name: options.find((x) => x.id === result.value).name, id: newLink.id, isDefault: false });
            }
            modalRef.hide();
        });
    }

    async deleteLink(id: number) {
        const result = await confirm(`Are you sure you want to delete this option from this winch stand?`, `${this.appInfo.settings.company}`);
        if (!result) return;

        const link = await this.uow.partConfigWinchStandOptionLinks.byId(id);
        if (link) {
            link.deleted = new Date();
        }
        this.winchStand._options = this.winchStand._options.filter((x) => x.id !== id);
    }

    async toggleDefault(id: number) {
        const link = this.winchStand.partConfigWinchStandOptionLinks.find((x) => x.id === id);
        const wasDefault = link?.isDefault;
        if (link) {
            this.winchStand.partConfigWinchStandOptionLinks.forEach((l) => {
                l.isDefault = false;
            });
            if (!wasDefault) {
                link.isDefault = true;
            }
            this.winchStand._options.forEach((o) => {
                o.isDefault = !wasDefault && o.id === id;
            });
        }
    }

    async save() {
        if (!this.validate()) return;
        this.result.next({ success: true, value: this.winchStand } as any);
    }

    close() {
        this.uow.rollback();
        this.result.next({ success: false });
    }

    validate() {
        const groupConfig = validationEngine.getGroupConfig('validationGroup');
        if (groupConfig) {
            const result = validationEngine.validateGroup('validationGroup');
            if (!result.isValid) {
                let message = '';
                result.brokenRules.forEach((brokenRule) => {
                    if (brokenRule.type === 'custom') message = `${brokenRule.message}\n${message}`;
                });
                this.appInfo.notifyToast(`Please fix errors before saving.\n\n${message}`, 'error');
                return false;
            }
        }
        return true;
    }
}
